import { Check, ShieldCheck } from 'lucide-react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { PASSWORD_MAX_SCORE, PASSWORD_RULES, PASSWORD_SECURE_SCORE } from '../constants/auth.constants'
import type { PasswordChecks } from '../utils/auth.utils'

type Variant = 'full' | 'compact'

// Labels are translation keys under auth.passwordStrength, resolved at render.
const FULL_LABELS: Record<'weak' | 'fair' | 'strong', { text: string; color: string }> = {
  weak: { text: 'auth.passwordStrength.weak', color: 'text-error-500' },
  fair: {
    text: 'auth.passwordStrength.fair',
    color: 'text-warning-600 dark:text-warning-500',
  },
  strong: {
    text: 'auth.passwordStrength.strong',
    color: 'text-success-600 dark:text-success-500',
  },
}

const COMPACT_LABELS: Record<'weak' | 'fair' | 'strong', { text: string; color: string }> = {
  weak: { text: 'auth.passwordStrength.weakShort', color: 'text-error-500' },
  fair: {
    text: 'auth.passwordStrength.fairShort',
    color: 'text-warning-600 dark:text-warning-500',
  },
  strong: {
    text: 'auth.passwordStrength.strongShort',
    color: 'text-success-600 dark:text-success-500',
  },
}

const RULE_ITEMS: Array<{ key: keyof PasswordChecks; label: string }> = [
  { key: 'minLength', label: 'auth.passwordStrength.ruleMinLength' },
  { key: 'uppercase', label: 'auth.passwordStrength.ruleUppercase' },
  { key: 'lowercase', label: 'auth.passwordStrength.ruleLowercase' },
  { key: 'number', label: 'auth.passwordStrength.ruleNumber' },
  { key: 'special', label: 'auth.passwordStrength.ruleSpecial' },
]

/**
 * "Strong" is the same bar the form gates on, not one point below it.
 *
 * `getPasswordScore` weighs the length rule 2 and each character-class rule 1,
 * so a password missing exactly one class scores 5 out of 6. These read `<= 4`
 * for "fair", which made 5 strong: `Inmeet123` has no special character, and
 * the meter answered "😎 Awesome! You have a secure password." in green while
 * the checklist below it still showed "Special character" unticked and the
 * submit button stayed disabled. Anchor on PASSWORD_SECURE_SCORE — the exact
 * threshold `isPasswordSecure` uses — so the meter cannot claim secure before
 * every configured requirement is met, and stays right if the rules change.
 */
function getLabel(score: number, variant: Variant) {
  const set = variant === 'full' ? FULL_LABELS : COMPACT_LABELS
  if (score === 0) return null
  if (score >= PASSWORD_SECURE_SCORE) return set.strong
  if (score <= 2) return set.weak
  return set.fair
}

function getBarColor(index: number, score: number): string {
  if (score === 0 || index >= score) return 'bg-stroke-200 dark:bg-stroke-500'
  if (score >= PASSWORD_SECURE_SCORE) return 'bg-success-500'
  if (score <= 2) return 'bg-error-500'
  return 'bg-warning-500'
}

interface PasswordStrengthMeterProps {
  score: number
  checks?: PasswordChecks
  isSecure?: boolean
  variant?: Variant
}

export default function PasswordStrengthMeter({
  score,
  checks,
  isSecure,
  variant = 'full',
}: PasswordStrengthMeterProps) {
  const t = useTranslation()
  const label = getLabel(score, variant)
  const barClass = variant === 'full' ? 'h-2 rounded-sm' : 'h-1 rounded-full'
  const barGap = variant === 'full' ? 'gap-1.5' : 'gap-1'

  return (
    <div className={variant === 'full' ? 'mt-2 space-y-2' : 'mt-2 space-y-1'}>
      <div className={`flex ${barGap}`}>
        {Array.from({ length: PASSWORD_MAX_SCORE }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 transition-colors duration-300 ${barClass} ${getBarColor(i, score)}`}
          />
        ))}
      </div>

      {label && (
        <p
          className={`${
            variant === 'full' ? 'text-sm' : 'text-xs'
          } font-medium flex items-center gap-1.5 ${label.color}`}
        >
          {variant === 'full' && isSecure && <ShieldCheck className="w-4 h-4" />}
          {t(label.text)}
        </p>
      )}

      {variant === 'full' && checks && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
          {RULE_ITEMS.map((item) => {
            const ok = checks[item.key]
            return (
              <div key={item.key} className="flex items-center gap-1.5 text-xs">
                <span
                  className={`inline-flex h-4 w-4 items-center justify-center rounded-sm border ${
                    ok
                      ? 'border-success-500 bg-success-500/15 text-success-600 dark:text-success-500'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  {ok ? <Check className="h-3 w-3" /> : ''}
                </span>
                <span className={ok ? 'text-foreground' : 'text-muted-foreground'}>
                  {t(item.label, { count: PASSWORD_RULES.MIN_LENGTH })}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
